import React, { useState, useEffect } from "react";
import { FaPlus, FaCheckCircle } from "react-icons/fa";
import { Trash2, Edit, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import "bootstrap/dist/css/bootstrap.min.css";

const HospitalPurchase = () => {
  const [searchTerm, setSearchTerm] = useState("");
  // eslint-disable-next-line
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line
  const [showEntries, setShowEntries] = useState("All");
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(1);

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/hospital_purchase`);
      setPurchases(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching hospital purchases:", error.message);
      setError("Failed to fetch purchase data. Please try again.");
      setTimeout(() => setError(null), 3000);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchPurchases();
  }, []);

  const itemsSummary = (items) =>
    (items || []).map((i) => i.item_name).join(", ") || "-";
  const totalQty = (items) =>
    (items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);

  const filteredPurchases = purchases.filter(
    (purchase) =>
      (purchase.department || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (purchase.vendor_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      itemsSummary(purchase.items)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (purchase.purchase_id || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const entriesPerPage =
    showEntries === "All" ? filteredPurchases.length || 1 : Number(showEntries);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredPurchases.length / entriesPerPage)
  );
  const indexOfLast = activePage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentPurchases = filteredPurchases.slice(indexOfFirst, indexOfLast);

  // Generate pagination with ellipsis logic
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      setActivePage(page);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${Apiurl}/hospital_purchase/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      fetchPurchases();
    } catch (error) {
      console.error("Error deleting purchase:", error.message);
      setError("Failed to delete purchase. Please try again.");
      setTimeout(() => setError(null), 3000);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="container-fluid p-3">
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Header */}
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-sm text-white"
          style={{ backgroundColor: "#01AEBE" }}
          onClick={() => navigate("/hospital-purchases/add-hospital-purchase")}
        >
          <FaPlus className="me-2" />
          Add Purchase
        </button>
      </div>

      {/* Main Table Card */}
      <div className="card">
        <div className="card-body">
          {/* Controls */}
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    setActivePage(1);
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      setActivePage(1);
                      setSearchTerm(e.target.value);
                    }}
                  />
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "40%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table responsive-table">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Purchase ID</th>
                  <th>Date</th>
                  <th>Department</th>
                  <th>Vendor Name</th>
                  <th>Items</th>
                  <th>Qty</th>
                  <th>Net Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : currentPurchases.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      No purchases found.
                    </td>
                  </tr>
                ) : (
                  currentPurchases.map((purchase, index) => (
                    <tr key={purchase._id} className="align-middle">
                      <td>{indexOfFirst + index + 1}</td>
                      <td>{purchase.purchase_id}</td>
                      <td>{purchase.po_date}</td>
                      <td>{purchase.department}</td>
                      <td>{purchase.vendor_name}</td>
                      <td>{itemsSummary(purchase.items)}</td>
                      <td>{totalQty(purchase.items)}</td>
                      <td>
                        ₹{(purchase.net_total || 0).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill fw-semibold px-3 py-2 ${
                            purchase.payment_status === "Paid"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          } d-inline-flex align-items-center gap-1`}
                        >
                          <FaCheckCircle />
                          {purchase.payment_status || "Pending"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm p-1 border-0"
                          style={{ backgroundColor: "transparent" }}
                          onClick={() => openDeleteModal(purchase._id)}
                        >
                          <Trash2 size={16} className="text-danger" />
                        </button>
                        <button
                          className="btn btn-sm p-1 border-0"
                          style={{ backgroundColor: "transparent" }}
                          onClick={() =>
                            navigate(
                              `/hospital-purchases/update-hospital-purchase/${purchase._id}`
                            )
                          }
                        >
                          <Edit size={16} className="text-primary" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="container">
            <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => activePage > 1 && setActivePage(activePage - 1)}
                disabled={activePage === 1}
              >
                <i className="fas fa-chevron-left"></i>
                <span className="d-none d-sm-inline">Previous</span>
              </button>
              <div
                className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                style={{ maxWidth: "100%" }}
              >
                {pages.map((page, index) => (
                  <button
                    key={index}
                    className={`btn btn-sm px-3 flex-shrink-0 ${
                      page === activePage
                        ? "text-white"
                        : "btn-outline-secondary"
                    }`}
                    style={
                      page === activePage
                        ? { backgroundColor: "#20c997", border: "none" }
                        : {}
                    }
                    onClick={() => handlePageClick(page)}
                    disabled={page === "..."}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() =>
                  activePage < totalPages && setActivePage(activePage + 1)
                }
                disabled={activePage === totalPages}
              >
                <span className="d-none d-sm-inline">Next</span>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete this purchase record?
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalPurchase;
